import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { MuiOtpInput } from "mui-one-time-password-input";
import { usePostHog } from "posthog-js/react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import PhoneNumberInput from "../Common/PhoneNumberInput";
import { MockBubbles } from "../MockBubbles";
import { PrimaryButton } from "../StyledComponents";
import { completePhoneSignup } from "../user/userSlice";
import { AppStoreButton } from "./AppStoreButton";
import CSRFToken from "./CSRFToken";

const COUNTRY_BLOCKED_ERROR_MESSAGE =
  "Signing up with phone number is not supported in this country";

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  position: "relative",
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  visibility: "visible",
  right: 0,
  left: 0,
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.text.secondary,
  "&.Mui-checked": {
    color: "#E4D4A0",
  },
  "& .MuiSvgIcon-root": {
    color: "#FFFFFF",
  },
}));

const SignUp = () => {
  const location = useLocation();
  const [phoneNumber, setPhoneNumber] = useState(""); // Ensure phoneNumber is initialized as an empty string
  const [smsConsent, setSmsConsent] = useState(false); // Changed to false by default
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [firstName, setFirstName] = useState("");
  const [interestedIn, setInterestedIn] = useState("female");
  const [focusAreas, setFocusAreas] = useState([]);
  const [balancedTechniques, setBalancedTechniques] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [userAddress, setUserAddress] = useState("");
  const [validationErrors, setValidationErrors] = useState({
    firstName: false,
    location: false,
  });
  const [open, setOpen] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [fprVisitorId, setFprVisitorId] = useState(null);
  const [country, setCountry] = useState("US");
  const [referralCode, setReferralCode] = useState(null);
  const [verificationUuid, setVerificationUuid] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const lastProcessedValue = useRef("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const phoneParam = params.get("phone");
    const fprParam = params.get("fpr");
    const refParam = params.get("ref");

    if (phoneParam) {
      setPhoneNumber(phoneParam);
    }
    if (fprParam) {
      console.log("FirstPromoter ID from URL:", fprParam);
      setFprVisitorId(fprParam);
    }
    if (refParam) {
      console.log("Referral code from URL:", refParam);
      setReferralCode(refParam);
    }

    posthog.capture("signup_page_view");
  }, [location]);

  useEffect(() => {
    let timer;
    if (step === 1 && !canResend && resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [canResend, resendTimer, step]);

  useEffect(() => {
    const fetchBalancedTechniques = async () => {
      try {
        const response = await axios.get("/api/analyze/available_techniques/");
        setBalancedTechniques(response.data);
      } catch (error) {
        console.error("Error fetching balanced techniques:", error);
      }
    };

    fetchBalancedTechniques();
  }, []);

  useEffect(() => {
    const fetchInitialTechniques = async () => {
      try {
        const response = await axios.get("/api/analyze/initial_techniques/");
        const initialTechniques = response.data.map(
          (technique) => technique.name
        );
        setFocusAreas(initialTechniques);
      } catch (error) {
        console.error("Error fetching initial techniques:", error);
        // Fallback to hardcoded values if the API call fails
        setFocusAreas(["Humor", "Asking Open-Ended Questions", "Storytelling"]);
      }
    };

    fetchInitialTechniques();
  }, []);

  // Debug logging
  useEffect(() => {
    console.log("FirstPromoter ID state:", fprVisitorId);
  }, [fprVisitorId]);

  const handleInitiateSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      posthog.capture("signup_continue_clicked", {
        country: country,
        phone_number: phoneNumber,
      });

      const response = await axios.post("/api/signup/phone/initiate/", {
        phone_number: phoneNumber,
        sms_consent: smsConsent,
        country: country,
      });

      if (response.status === 200) {
        setVerificationUuid(response.data.verification_uuid);
        setStep(1);
        setCanResend(false);
        setResendTimer(30);
        posthog.capture("signup_verification_step_reached", {
          country: country,
        });
      }
    } catch (error) {
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError("Error initiating sign-up. Please try again.");
      }
      Sentry.captureException(error, {
        extra: {
          phoneNumber,
          country,
        },
      });
      console.error("Sign-up initiation error:", error);
    }
    setLoading(false);
  };

  const handleVerificationCodeChange = (value) => {
    // If we have already processed this value, skip
    if (value === lastProcessedValue.current) {
      return;
    }
    lastProcessedValue.current = value;

    // Always set the verification code
    setVerificationCode(value);

    // When length is exactly 6, trigger login verification once
    if (value.length === 6) {
      handleVerifyPhone(value);
    }
  };

  const handleVerifyPhone = async (code) => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post("/api/signup/phone/verify/", {
        phone_number: phoneNumber,
        verification_code: code,
        verification_uuid: verificationUuid,
      });

      if (response.status === 200) {
        setStep(2);
        posthog.capture("signup_final_step_reached", {
          country: country,
        });
      }
    } catch (error) {
      setError("Error verifying phone number. Please try again.");
      console.error("Phone verification error:", error);
      Sentry.captureException(error, {
        extra: {
          phoneNumber,
          country,
        },
      });
    }
    setLoading(false);
  };

  const handleCompleteSignUp = async (e) => {
    e.preventDefault();

    // Only validate firstName
    setValidationErrors({
      firstName: !firstName,
      location: false,
    });

    if (!firstName) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await dispatch(
        completePhoneSignup({
          phone_number: phoneNumber,
          phone_country_code: country,
          first_name: firstName,
          interested_in: interestedIn,
          focus_areas: focusAreas,
          user_location: userLocation || null,
          user_address: userAddress || "",
          fpr_visitor_id: fprVisitorId,
          referral_code: referralCode,
        })
      ).unwrap();

      // Log successful signup completion
      posthog.capture("signup_completed_successfully", {
        country: country,
        has_referral: !!referralCode,
      });

      navigate("/", { state: { fromSignup: true } });
    } catch (error) {
      setError("Error completing sign-up. Please try again.");
      console.error("Sign-up completion error:", error);
      Sentry.captureException(error, {
        extra: {
          phoneNumber,
          country,
        },
      });
    }
    setLoading(false);
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    setLoading(true);
    setError("");

    try {
      posthog.capture("signup_resend_code_clicked", {
        country: country,
      });

      const response = await axios.post("/api/signup/phone/initiate/", {
        phone_number: phoneNumber,
        country: country,
      });

      if (response.status === 200) {
        setVerificationUuid(response.data.verification_uuid);
        setCanResend(false);
        setResendTimer(30);
      }
    } catch (error) {
      setError("Error resending verification code. Please try again.");
      console.error("Resend verification code error:", error);
      Sentry.captureException(error, {
        extra: {
          phoneNumber,
          country,
        },
      });
    }
    setLoading(false);
  };

  const getValidationErrors = () => {
    const errors = [];
    if (validationErrors.firstName) errors.push("First name");
    return errors;
  };

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
    if (validationErrors.firstName) {
      setValidationErrors((prev) => ({ ...prev, firstName: false }));
    }
  };

  const handleLocationChange = (newLocation, newAddress) => {
    setUserLocation(newLocation);
    setUserAddress(newAddress);
    if (validationErrors.location) {
      setValidationErrors((prev) => ({ ...prev, location: false }));
    }
  };

  // Add this handler to prevent form submission on Enter
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleCountryChange = (newCountry) => {
    setCountry(newCountry);
    setSmsConsent(false); // Reset the consent checkbox when the country changes
  };

  const getConsentText = () => {
    return "I consent to receive SMS text messages";
  };

  const getAgreementText = () => {
    return "I also agree to receive text messages at the phone number provided. Reply STOP to cancel. Msg rates may apply.";
  };

  const renderStep = () => {
    // Check for the specific error message about country not supported
    if (error && error.includes(COUNTRY_BLOCKED_ERROR_MESSAGE)) {
      return (
        <Stack spacing={3} alignItems="center">
          <Typography
            sx={{
              textAlign: "center",
              color: "#F53D3B",
              fontSize: { xs: "18px", md: "28px" },
              fontWeight: 500,
              mb: 1,
            }}
          >
            Download our app
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              color: "#FFFFFF",
              mb: 2,
            }}
          >
            {error}
          </Typography>

          <AppStoreButton />
        </Stack>
      );
    }

    switch (step) {
      case 0:
        return (
          <Stack spacing={2}>
            <Box>
              <Typography
                sx={{
                  textAlign: { xs: "center", md: "left" },
                  color: "#F53D3B",
                  fontSize: { xs: "18px", md: "28px" },
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                Get our app!
              </Typography>
              <Typography
                sx={{
                  textAlign: { xs: "center", md: "left" },
                  color: "#888",
                  fontWeight: 400,
                  fontSize: "14px",
                  mb: 1,
                }}
              >
                We're now exclusively on our mobile app. Download it for free
                to continue using Smoothspeak.
              </Typography>
            </Box>
            <Box>
              <AppStoreButton />
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#1C1C1E",
                  padding: { xs: "6px 12px", sm: "8px 16px" },
                  borderRadius: "20px",
                  margin: "32px auto 0",
                  width: "fit-content",
                }}
              >
                <a
                  href="https://www.instagram.com/smoothspeakai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <img
                    src="https://cdn.prod.website-files.com/6706e4607b856db89ebe457b/674d36920a51536aef34fa9b_5335781_camera_instagram_social%20media_instagram%20logo_icon.svg"
                    alt="Instagram"
                    style={{ width: "24px", height: "24px" }}
                  />
                </a>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#8E8E93",
                    marginLeft: 1,
                  }}
                >
                  Loved by{" "}
                  <span style={{ color: "#00A67E", fontWeight: "bold" }}>
                    50,000+
                  </span>{" "}
                  users
                </Typography>
              </Box>
            </Box>
          </Stack>
        );
      case 1:
        return (
          <>
            <Typography
              sx={{
                textAlign: { xs: "center", md: "left" },
                color: "#F53D3B",
                fontSize: { xs: "18px", md: "28px" },
                fontWeight: 500,
                mb: 1,
              }}
            >
              Verification
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter verification code
            </Typography>
            <form onSubmit={(e) => e.preventDefault()}>
              <MuiOtpInput
                value={verificationCode}
                onChange={handleVerificationCodeChange}
                length={6}
                validateChar={(value) => /^\d+$/.test(value)}
                TextFieldsProps={{
                  type: "tel",
                  placeholder: "",
                  size: "small",
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#2C2C2E",
                      color: "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#3A3A3C",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#3A3A3C",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#38383A",
                    },
                  },
                  inputProps: (index) => ({
                    type: "tel",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: index === 0 ? 6 : 1,
                    style: {
                      textAlign: "center",
                      color: "#FFFFFF",
                    },
                    autoComplete: "one-time-code",
                    textContentType: "oneTimeCode",
                  }),
                }}
                sx={{
                  gap: 1,
                  "& .MuiOutlinedInput-input": {
                    px: 0,
                  },
                }}
              />
            </form>
            <Button
              variant="text"
              sx={{
                mt: 1,
                color: "#CFC192",
                width: "auto",
                justifyContent: "flex-start",
                "&:hover": {
                  color: "#FF453A",
                },
                "&.Mui-disabled": {
                  color: "rgba(245, 61, 56, 0.5)",
                },
              }}
              onClick={handleResendCode}
              disabled={!canResend}
            >
              {canResend ? "Resend Code" : `Resend Code (${resendTimer}s)`}
            </Button>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => setStep(0)}
              sx={{
                mt: 1,
                color: "white",
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
              }}
            >
              Change phone number
            </Button>
          </>
        );
      case 2:
        return (
          <form
            onSubmit={handleCompleteSignUp}
            style={{
              paddingBottom: isMobile ? "40vh" : "0", // Add extra padding at bottom on mobile
            }}
          >
            <Typography
              sx={{
                textAlign: { xs: "center", md: "left" },
                color: "#F53D3B",
                fontSize: { xs: "18px", md: "28px" },
                fontWeight: 500,
                mb: 1,
              }}
            >
              Complete sign up
            </Typography>
            <Typography
              sx={{
                mb: 1,
                color: "#FFFFFF",
                fontWeight: 500,
                fontSize: "14px",
              }}
            >
              First name{" "}
              <Box component="span" sx={{ color: "error.main" }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your first name"
              value={firstName}
              onChange={handleFirstNameChange}
              required
              size={isMobile ? "small" : "medium"}
              InputProps={{
                style: {
                  fontSize: "16px", // Minimum 16px to prevent zoom on iOS
                  backgroundColor: "#2C2C2E",
                  color: "#FFFFFF",
                },
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "#38383A",
                  },
                  "&:hover fieldset": {
                    borderColor: "#48484A",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#FF3B30",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  "&::placeholder": {
                    color: "#8E8E93",
                    opacity: 1,
                  },
                },
              }}
            />
            <PrimaryButton
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Sign Up"}
            </PrimaryButton>

            {getValidationErrors().length > 0 && (
              <Typography
                color="error"
                variant="body2"
                align="center"
                sx={{ mt: 1 }}
              >
                Please fill in the following required fields:{" "}
                {getValidationErrors().join(", ")}
              </Typography>
            )}
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <MockBubbles />
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={() => {
            setOpen(false);
            setIsCollapsed(true);
          }}
          onOpen={() => {
            setOpen(true);
            setIsCollapsed(false);
          }}
          swipeAreaWidth={0}
          disableSwipeToOpen={true}
          disableDiscovery={true}
          disableSwipeToClose={true}
          variant="permanent"
          ModalProps={{
            keepMounted: true,
            BackdropProps: {
              sx: { backgroundColor: "transparent" },
            },
          }}
          PaperProps={{
            sx: {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              height: "auto",
              maxHeight: "95vh",
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              backgroundColor: "#1C1C1E",
              color: "#FFFFFF",
              pointerEvents: "auto",
              transform: "none !important",
            },
          }}
        >
          <StyledBox sx={{ backgroundColor: "#1C1C1E", color: "#FFFFFF" }}>
            <Box sx={{ p: 2, pb: 2 }}>
              <CardContent sx={{ width: "100%", pt: 0 }}>
                <Stack spacing={2}>
                  <CSRFToken />
                  {renderStep()}
                  {error && !error.includes(COUNTRY_BLOCKED_ERROR_MESSAGE) && (
                    <Typography
                      color="error"
                      align="center"
                      sx={{ fontSize: "12px" }}
                    >
                      {error}
                      <br />
                      <Box component="span" sx={{ fontSize: "12px" }}>
                        Contact{" "}
                        <Link href="mailto:support@smoothspeak.ai">
                          support@smoothspeak.ai
                        </Link>{" "}
                        if you continue to face issues
                      </Box>
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Box>
          </StyledBox>
        </SwipeableDrawer>
      ) : (
        <Container
          maxWidth="sm"
          sx={{
            position: "fixed",
            top: "20vh",
            left: "50%",
            transform: "translateX(-50%)",
            py: { xs: 0, md: 2 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pointerEvents: "auto",
          }}
        >
          <Box
            sx={{
              px: 4,
              py: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#1C1C1E",
              color: "#FFFFFF",
              borderRadius: 4,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.5)",
            }}
          >
            <CardContent sx={{ width: "100%" }}>
              <Stack spacing={2}>
                <CSRFToken />
                {renderStep()}
                {error && !error.includes(COUNTRY_BLOCKED_ERROR_MESSAGE) && (
                  <Typography color="error" align="center">
                    {error}
                    <br />
                    <Box component="span">
                      Contact{" "}
                      <Link href="mailto:support@smoothspeak.ai">
                        support@smoothspeak.ai
                      </Link>{" "}
                      if you continue to face issues
                    </Box>
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Box>
        </Container>
      )}
    </Box>
  );
};

export default SignUp;
